import { useMemo, type ReactNode } from "react";
import {
  Box,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  TabsList,
  TabsOverflow,
  TabsOverflowSpacer,
  TabsOverflowViewport,
  TabsTab,
  Text,
  ViewTabsRailButton,
  ViewTabsRailSeparator,
  type IconName,
  type Omittable,
} from "@salestrak/ui";

/**
 * The detail surfaces' shared tab strip (the project, Account and Contact
 * views): the pinned Summary anchor, a strong hairline, then the per-user
 * draggable tabs. The strip folds instead of clipping — tabs that stop
 * fitting (a narrow drawer, a squeezed window) leave the row for a quiet
 * "More" menu seated after the last visible tab, the same priority+ fold the
 * workflow shell's view rail uses (the library's `TabsOverflow`).
 *
 * A pick from the menu activates the tab AND promotes it into the last
 * visible slot — written through the same personal order the strip's own tab
 * drag persists — so it does not fold straight back on the next measure. The
 * trigger lights the active ink when a later squeeze folds the mounted tab
 * itself: the strip always shows where the reader is.
 *
 * A Menu, not the view rail's popover: these rows only activate — reordering
 * stays the strip's own drag — so menu semantics fit and nothing fights a
 * drag gesture.
 */

/** One sortable tab's visuals. Summary is the pinned anchor, rendered by the
 *  strip itself, so it has no row in a host's meta map. */
export type DetailTabMeta = { icon: IconName; label: string };

export function DetailViewTabsList<T extends string>({
  sortableTabs,
  meta,
  activeTab,
  onOrderChange,
  onSelectTab,
  renderTabExtra,
  actions,
}: Omittable<{
  /** Draggable tab values in display order — everything after the pinned
   *  Summary (`useDetailViewTabOrder`'s order minus the pinned set). */
  sortableTabs: T[];
  /** Icon + label per sortable tab value. Pass a module-level constant. */
  meta: Record<T, DetailTabMeta>;
  /** The mounted tab — lights the "More" trigger while it is folded. */
  activeTab: string;
  /** Persists a new drag order. The strip's own tab drag and the menu's
   *  promote-on-pick both write through it. */
  onOrderChange: (order: string[]) => void;
  /** A "More" pick activates the tab (never `"summary"` — the pinned anchor
   *  cannot fold). The promote reorder is this strip's own job. */
  onSelectTab: (tab: T) => void;
  /** Trailing adornment inside one tab's label — a count, a badge. */
  renderTabExtra?: (tab: T) => ReactNode;
  /** The strip's ONE action, seated at its end: the surface's primary verb
   *  (the follow-up Update / Start), the same spot on every detail view so
   *  the verb is found by position. It sits outside the fold viewport, so
   *  tabs fold around it rather than under it. Not a toolbar — view tools
   *  belong to the body below, never to this rail. */
  actions?: ReactNode;
}>) {
  // The overflow measure lines widths up with values by index, so its order
  // is the FULL strip — pinned anchor included — while the sortable order
  // stays the draggable subset.
  const stripOrder = useMemo(
    () => ["summary", ...sortableTabs],
    [sortableTabs],
  );

  return (
    <Box align="center" direction="row" gap="xs" minWidth={0}>
      <TabsOverflow order={stripOrder}>
        {({ hidden }) => {
          const hiddenSet = new Set(hidden);
          // Only sortables can fold (the measure never hides the strip's
          // first tab), so the narrowing drops nothing real.
          const hiddenTabs = sortableTabs.filter((tab) => hiddenSet.has(tab));
          const promoteTab = (tab: T) => {
            onSelectTab(tab);
            const rest = sortableTabs.filter((t) => t !== tab);
            const at = Math.max(0, sortableTabs.length - hiddenTabs.length - 1);
            onOrderChange([...rest.slice(0, at), tab, ...rest.slice(at)]);
          };
          return (
            <>
              <TabsOverflowViewport>
                <TabsList
                  onOrderChange={onOrderChange}
                  order={sortableTabs}
                  variant="underline"
                >
                  <TabsTab value="summary">
                    <Icon name="layout-dashboard" size="md" />
                    Summary
                  </TabsTab>
                  {/* The hairline marks where the pinned anchor ends and the
                      draggable set begins — `strong`, because this boundary
                      is meant to be found, not just felt. */}
                  <ViewTabsRailSeparator tone="strong" />
                  {sortableTabs.map((tab) => (
                    <TabsTab
                      key={tab}
                      overflowHidden={hiddenSet.has(tab)}
                      value={tab}
                    >
                      <Icon name={meta[tab].icon} size="md" />
                      {meta[tab].label}
                      {renderTabExtra?.(tab)}
                    </TabsTab>
                  ))}
                </TabsList>
              </TabsOverflowViewport>
              {hiddenTabs.length > 0 ? (
                <Menu>
                  <MenuTrigger
                    render={
                      <ViewTabsRailButton
                        active={hiddenSet.has(activeTab)}
                        aria-label={`${hiddenTabs.length} more tabs`}
                      />
                    }
                  >
                    More
                    <Icon name="chevron-down" size="sm" />
                  </MenuTrigger>
                  <MenuPopup align="start" side="bottom">
                    {/* Rows repeat the tab's own metrics — 16px glyph, 14px
                        semibold label — so a folded tab reads as the same
                        object it was in the strip, not a menu-sized restyle
                        of it. */}
                    {hiddenTabs.map((tab) => (
                      <MenuItem key={tab} onClick={() => promoteTab(tab)}>
                        <Icon name={meta[tab].icon} size="md" />
                        <Text size="sm" weight="semibold">
                          {meta[tab].label}
                        </Text>
                      </MenuItem>
                    ))}
                  </MenuPopup>
                </Menu>
              ) : null}
              <TabsOverflowSpacer />
            </>
          );
        }}
      </TabsOverflow>
      {actions ? (
        <Box align="center" direction="row" gap="xs" shrink={false}>
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}
